import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

/**
 * Theme selection. Default is the OS preference (`prefers-color-scheme`);
 * the user can override to an explicit `light` or `dark` which persists to
 * localStorage. Removing the stored value (via `toggle` resolving back to
 * system) is *not* exposed — once the user clicks, we remember the choice;
 * it's simple and matches what users expect from a one-click toggle.
 *
 * The inline bootstrap script in `index.html` reads the same localStorage
 * key to set the initial `<html class="dark">` before React mounts, which
 * avoids a flash of the wrong theme on first paint. Keep them in sync.
 */
export type ThemeOverride = 'light' | 'dark' | null
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'theme'

interface ThemeContextValue {
  resolved: ResolvedTheme
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readStoredOverride(): ThemeOverride {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark') return raw
  } catch (err) {
    // localStorage may be disabled (privacy mode, quota). Warn so we leave
    // a breadcrumb, then fall through to the OS preference.
    console.warn('[theme] localStorage read failed; falling back to system preference', err)
  }
  return null
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function resolve(override: ThemeOverride): ResolvedTheme {
  if (override === 'light' || override === 'dark') return override
  return systemPrefersDark() ? 'dark' : 'light'
}

function apply(resolved: ResolvedTheme) {
  const root = document.documentElement
  if (resolved === 'dark') root.classList.add('dark')
  else root.classList.remove('dark')
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<ThemeOverride>(() => readStoredOverride())
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(override))

  useEffect(() => {
    const next = resolve(override)
    setResolved(next)
    apply(next)
  }, [override])

  // Track OS changes while the user has no explicit override so the UI
  // flips live when the user changes their system appearance.
  useEffect(() => {
    if (override !== null || typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      const next: ResolvedTheme = e.matches ? 'dark' : 'light'
      setResolved(next)
      apply(next)
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [override])

  const toggle = () => {
    const next: ThemeOverride = resolved === 'dark' ? 'light' : 'dark'
    setOverride(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch (err) {
      // Persistence is best-effort; state still holds for the session.
      console.warn('[theme] localStorage write failed; preference will not persist', err)
    }
  }

  return createElement(ThemeContext.Provider, { value: { resolved, toggle } }, children)
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>')
  return ctx
}
